'use client';
import React from 'react';
import { Filter, Filters, matchFilterAttributes } from '@benzinga/filter-ui';
import { Product } from '@benzinga/content-manager';

import type { ColumnType } from 'antd/lib/table';

import styled from '@benzinga/themetron';
import { Layout } from '@benzinga/core-ui';
import { Table } from 'antd';
import { sanitizeHTML } from '@benzinga/frontend-utils';

const CompareProductTable = React.lazy(() =>
  import('@benzinga/product-ui').then(module => ({ default: module.CompareProductTable })),
);

export const ProductsListBlock = ({ attrs }) => {
  const [products] = React.useState<Product[]>((attrs?.data?.products as Product[]) || []);
  const [filters, setFilters] = React.useState<Filter[]>(attrs?.data?.filters || []);
  const [selectedFilters, setSelectedFilters] = React.useState<Set<string>>(new Set());

  const handleFilterChange = React.useCallback(
    (filter: Filter) => {
      const updatedFilters = filters.map(f => f);
      setFilters(updatedFilters);

      const selected = new Set([...selectedFilters]);

      if (Array.isArray(filter.selected) && filter.selected.length) {
        selected.add(filter.key);
      } else if (!Array.isArray(filter.selected) && filter.selected) {
        selected.add(filter.key);
      } else {
        selected.delete(filter.key);
      }

      setSelectedFilters(selected);
    },
    [filters, selectedFilters],
  );

  const filteredProducts = React.useCallback((): Product[] => {
    if (!Array.isArray(products)) {
      return [];
    }

    if (!selectedFilters?.size) {
      return products;
    }

    return products.filter(product => {
      return product.data ? matchFilterAttributes(filters, selectedFilters, product.data) : false;
    });
  }, [filters, selectedFilters, products]);

  const formatTableColumn = (dataItemKey, data): ColumnType<any> => {
    return {
      dataIndex: dataItemKey,
      key: dataItemKey,
      render: (value, data) => {
        if (!data?.data?.[dataItemKey]) {
          return value;
        }

        return <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(data.data[dataItemKey].display_value) }} />;
      },
      sortDirections: ['ascend', 'descend'],
      sorter: data[dataItemKey]['sorting'] ? (a, b) => a[dataItemKey] - b[dataItemKey] : false,
      title: data[dataItemKey].label,
    };
  };

  const getTableColumns = React.useCallback(() => {
    if (attrs?.data?.layout !== 'table') {
      return [];
    }

    if (Array.isArray(products) && products.length) {
      const columns: ColumnType<any>[] = [];

      const data = products[0].data;
      for (const dataItemKey in data) {
        columns.push(formatTableColumn(dataItemKey, data));
      }

      return columns;
    }

    return [];
  }, [products, attrs.data.layout]);

  return (
    <ProductsListsBlock>
      {attrs?.data?.layout === 'card' ? (
        <Layout
          className="offerings-page-layout"
          layoutLeftSidebar={<Filters filters={filters} onChange={handleFilterChange} />}
          layoutMain={
            <div className="compare-product-table-wrapper">
              <CompareProductTable products={filteredProducts()} />
            </div>
          }
        />
      ) : (
        <TableWrapper>
          <Filters filters={filters} layout="horizontal" onChange={handleFilterChange} />
          <React.Suspense>
            <Table
              columns={getTableColumns()}
              dataSource={filteredProducts()}
              pagination={false}
              rowKey="id"
              scroll={{ x: 1000, y: 600 }}
              showSorterTooltip={false}
            />
          </React.Suspense>
        </TableWrapper>
      )}
    </ProductsListsBlock>
  );
};

const ProductsListsBlock = styled.div`
  .compare-product-table-wrapper {
    width: 100%;
  }
`;

const TableWrapper = styled.div`
  margin-bottom: 25px;

  .ant-table-wrapper {
    border: solid 1px #ddd;
    margin-top: 1rem;
  }
`;

export default ProductsListBlock;
